import {createContext, useContext, useEffect, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import axiosPublic from "../api/axiosPublic";
import jwtDecode from "jwt-decode";
import {useSnackbar} from "notistack";
import usePublicQueries from "../hooks/queries/usePublicQueries";
import Loading from "../components/Loading";


export const AuthContext = createContext(null);

export const AuthProvider = ({children}) => {
    const queryClient = useQueryClient()
    const {enqueueSnackbar} = useSnackbar()

    const {
        loginMutation,
        registerMutation,
        forgetPasswordMutation,
        registerForBusinessMutation,
        newPasswordMutation
    } = usePublicQueries()

    const [loading, setLoading] = useState(true)
    const [auth, setAuth] = useState({
        access: null,
        refresh: null,
    });

    const getUser = () => {
        if (auth?.access) return jwtDecode(auth.access)
        return null
    }

    // Load tokens on mount
    useEffect(() => {
        const access = localStorage.getItem("access");
        const refresh = localStorage.getItem("refresh");

        setAuth({
            access,
            refresh,
        });
        setLoading(false)
    }, []);

    const login = async (email, password) => {
        loginMutation.mutate({email, password}, {
            onSuccess: response => {
                const {access, refresh} = response
                const newAuth = {access, refresh}
                setAuth(newAuth);
                localStorage.setItem("access", access);
                localStorage.setItem("refresh", refresh);
                queryClient.clear();
            },
        })
    };

    const registerUser = (data) => {
        if (data.password !== data.password_again) {
            enqueueSnackbar("Nem egyezik a két jelszó!", {variant: 'error'})
            return
        }
        registerMutation.mutate(data, {
            onError: response => {
                enqueueSnackbar(response?.response?.data?.message||'Váratlan hiba!', {variant: 'error'});
            }
        })
    }

    const registerExistingUser = (data) => {
        if (data?.password !== data?.password_again) {
            enqueueSnackbar('Nem egyezik a két jelszó!', {variant: 'error'})
            return
        }
        registerForBusinessMutation.mutate(data)
    }

    const forgotPassword = (data) => forgetPasswordMutation.mutate(data)

    const newPassword = (data) => newPasswordMutation.mutate(data)

    const updateToken = async () => {
        if (auth.refresh) {
            const response = await axiosPublic.post('/api/token/refresh/', {refresh: auth.refresh})
            const {access, refresh} = response.data;
            const newAuth = {access, refresh}
            setAuth(newAuth)
            localStorage.setItem("access", access);
            localStorage.setItem("refresh", refresh);
            return newAuth
        }
        logout()
    }

    const logout = () => {
        setAuth({access: null, refresh: null});
        localStorage.removeItem("access");
        localStorage.removeItem("refresh");
        queryClient.clear();
    };

    return (
        <AuthContext.Provider value={{
            auth,
            loading,
            getUser,
            login,
            logout,
            updateToken,
            registerUser,
            registerExistingUser,
            forgotPassword,
            newPassword,
            isAuthenticated: !!auth.access
        }}>
            {children}
            <Loading isLoading={
                loginMutation?.isPending ||
                registerMutation?.isPending ||
                forgetPasswordMutation.isPending ||
                registerForBusinessMutation.isPending}/>
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used inside <AuthProvider>");
    return context;
};